import { useEffect, useState } from 'react';
import { fetchHandbookSections, type Block, type HandbookSectionRow } from '../services/helpContentService';
import { getErrorMessage } from '../utils/errors';

export type SubSectionType = { id: string; number: string; title: string; screenshot?: string; blocks: Block[] };

export type Section = {
    id: string;
    number: string;
    title: string;
    screenshot?: string;
    blocks: Block[];
    sub?: SubSectionType[];
};

function buildTree(rows: HandbookSectionRow[]): Section[] {
    const topLevel = rows.filter(r => r.parent_key === null);
    const subsByParent = new Map<string, HandbookSectionRow[]>();

    for (const row of rows) {
        if (row.parent_key === null) continue;
        const list = subsByParent.get(row.parent_key) ?? [];
        list.push(row);
        subsByParent.set(row.parent_key, list);
    }

    return topLevel.map(row => {
        const subs = subsByParent.get(row.section_key);
        const section: Section = {
            id: row.section_key,
            number: row.number,
            title: row.title,
            screenshot: row.screenshot ?? undefined,
            blocks: row.blocks,
        };
        if (subs && subs.length > 0) {
            section.sub = subs
                .sort((a, b) => a.sort_order - b.sort_order)
                .map(sub => ({
                    id: sub.section_key,
                    number: sub.number,
                    title: sub.title,
                    screenshot: sub.screenshot ?? undefined,
                    blocks: sub.blocks,
                }));
        }
        return section;
    });
}

export function useOperationsHandbook() {
    const [sections, setSections] = useState<Section[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(null);
            try {
                const rows = await fetchHandbookSections();
                if (!cancelled) setSections(buildTree(rows));
            } catch (err) {
                if (!cancelled) setError(getErrorMessage(err, 'Failed to load help content'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, []);

    return { sections, loading, error };
}